import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { useAuth } from "../context/AuthContext";
import { Sparkles } from "lucide-react";
import { errMsg } from "../lib/api";
import { toast } from "sonner";

export default function AuthDialog({ open, onOpenChange }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back, Trainer.");
      } else {
        await register(form.email, form.password, form.name || "Trainer");
        toast.success("Account created — 100 Kavo Points are yours!");
      }
      onOpenChange(false);
      setForm({ email: "", password: "", name: "" });
    } catch (e) {
      setError(errMsg(e, "Authentication failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="auth-dialog"
        className="bg-[#0A0812] border border-white/10 text-white max-w-md p-0 overflow-hidden"
      >
        <div className="px-8 pt-8 pb-2">
          <DialogHeader>
            <DialogTitle asChild>
              <h2 className="font-display text-3xl text-white tracking-tight">
                {mode === "login" ? "Welcome back" : "Join the Vault"}
              </h2>
            </DialogTitle>
          </DialogHeader>
          <p className="mt-2 text-sm text-white/60">
            {mode === "login"
              ? "Sign in to redeem Kavo Points and track your orders."
              : "Create an account and earn 100 Kavo Points instantly."}
          </p>
        </div>

        <form onSubmit={submit} className="px-8 pb-8 space-y-4 mt-4">
          {mode === "register" && (
            <div>
              <label className="block text-[11px] tracking-[0.22em] uppercase text-white/50 font-bold mb-2">Name</label>
              <input
                type="text"
                value={form.name}
                onChange={set("name")}
                data-testid="auth-name-input"
                placeholder="Ash Ketchum"
                className="w-full bg-[#110E1D] border border-white/10 rounded-md px-4 py-3 text-sm focus:border-[#B026FF]/60 outline-none"
              />
            </div>
          )}
          <div>
            <label className="block text-[11px] tracking-[0.22em] uppercase text-white/50 font-bold mb-2">Email</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={set("email")}
              data-testid="auth-email-input"
              placeholder="trainer@kavocards.com"
              className="w-full bg-[#110E1D] border border-white/10 rounded-md px-4 py-3 text-sm focus:border-[#B026FF]/60 outline-none"
            />
          </div>
          <div>
            <label className="block text-[11px] tracking-[0.22em] uppercase text-white/50 font-bold mb-2">Password</label>
            <input
              type="password"
              required
              minLength={6}
              value={form.password}
              onChange={set("password")}
              data-testid="auth-password-input"
              placeholder="••••••••"
              className="w-full bg-[#110E1D] border border-white/10 rounded-md px-4 py-3 text-sm focus:border-[#B026FF]/60 outline-none"
            />
          </div>

          {error && (
            <div data-testid="auth-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="auth-submit-btn"
            className="btn-violet w-full h-12 rounded-md font-medium tracking-wide flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {busy ? "Loading…" : mode === "login" ? "Sign in" : "Create account"}
            {mode === "register" && <Sparkles size={16} />}
          </button>

          <div className="text-center text-sm text-white/60">
            {mode === "login" ? (
              <>
                New to Kavo?{" "}
                <button
                  type="button"
                  data-testid="auth-switch-register"
                  onClick={() => { setMode("register"); setError(""); }}
                  className="text-[#B026FF] hover:underline font-medium"
                >
                  Create an account
                </button>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <button
                  type="button"
                  data-testid="auth-switch-login"
                  onClick={() => { setMode("login"); setError(""); }}
                  className="text-[#B026FF] hover:underline font-medium"
                >
                  Sign in
                </button>
              </>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
